import { playerTimeState } from '@audacy-clients/core/atoms/player';
import { secondsToMilliseconds } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Constants } from './styles';
import jsonData from './transcript_sample';
import { type TranscriptData, type Transcript } from '.';

type UseTranscriptReturnType = {
  transcript: Array<Transcript> | undefined;
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  duration: number;
  offsetEqualToZero: boolean;
  isTranscriptAvailable: boolean;
};

/**
 * Handles the transcript data and the current index of the transcript
 */
const useTranscript = (): UseTranscriptReturnType => {
  const [data, setData] = useState<TranscriptData>();
  const [currentIndex, setCurrentIndex] = useState(Constants.noIndexFounded);

  const { duration = 0, offset = Constants.startOffset } = useRecoilValue(playerTimeState);

  const prevIndexRef = useRef<number | null>(null);

  const offsetInMilliseconds = secondsToMilliseconds(offset);
  const offsetEqualToZero = offsetInMilliseconds === 0;

  const transcript = useMemo(() => data?.transcript, [data?.transcript]);

  useEffect(() => {
    const fetchData = () => {
      // const jsonData = require('.transcript_sample.json') as TranscriptData;
      setData(jsonData);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!transcript) {
      return;
    }

    const newIndex = transcript.findIndex(
      (item) =>
        offsetInMilliseconds >= getRoundedMs(item.start_time) &&
        offsetInMilliseconds < getRoundedMs(item.end_time),
    );

    if (newIndex === Constants.noIndexFounded && prevIndexRef.current !== null) {
      setCurrentIndex(prevIndexRef.current);
    } else {
      prevIndexRef.current = newIndex;
      setCurrentIndex(newIndex);
    }
  }, [transcript, offsetInMilliseconds]);

  // Helper function to check if transcript is valid
  const isTranscriptAvailable = useMemo(() => {
    return !!transcript && transcript.length > 0;
  }, [transcript]);

  return {
    transcript,
    currentIndex,
    setCurrentIndex,
    duration,
    offsetEqualToZero,
    isTranscriptAvailable
  };
};

export default useTranscript;

const getRoundedMs = (time: number) => {
  return Math.floor(time / 1000) * 1000;
};
